import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, QueryList, ViewChildren, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatePicker, DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { CompanySelectionService } from '@core/service/company-selection.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { CompanySelectComponent } from '@shared/components/company-select/company-select.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { AppConfigService } from 'app/AppConfigService.service';
import {
  ATTENDANCE_ALLOWED_SOURCE_OPTIONS,
  ATTENDANCE_SOURCE_OVERRIDE_OPTIONS,
  ATTENDANCE_TRACKING_INTERVAL_PRESET_SECONDS,
  ATTENDANCE_TIMEZONE_OPTIONS,
  AttendanceConfigFormValue,
  AttendanceConfigListResponse,
  AttendanceConfigRecord,
  AttendanceSourceRuleConfig,
  AttendanceSourceRuleFormValue,
  AttendanceZoneListResponse,
  AttendanceZoneOption,
  buildAttendanceConfigPayload,
  createEmptyAttendanceConfigForm,
  createEmptyAttendanceSourceRuleForm,
  findAttendanceConfigForScope,
  extractAttendanceZones,
  formatAttendanceTrackingInterval,
  getAttendanceSourceLabelKey,
  mapAttendanceConfigToForm,
  usesDeviceRules,
  usesDuplicateWindow,
  usesGeo,
  usesMistypedNormalization,
  usesTimezone,
  usesUntypedNormalization,
  validateAttendanceConfigForm,
} from '@shared/utils/attendance-config.util';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { WorkCycleConfig } from '../../attendances/work-cycle/work-cycle.interface';
import { DailyScheduleConfig } from '../../attendances/daily-schedule/daily-schedule.interface';
import { DeviceRecord, DevicesResponse } from '../../devices/device.interface';
import {
  EmployeeBiometricTemplateRecord,
  EmployeeBiometricTemplatesResponse,
  EmployeeFaceProfileDeleteResponse,
  EmployeeFaceProfileRecord,
  EmployeeRecord,
  EmployeeUserProvisioning,
  EmployeeWithLaborConfigPayload,
  EmployeeWithLaborConfigResponse,
  EmployeeWorkAssignment,
} from './employee.interface';
import { RestConfigurationConfig } from '../../attendances/rest-configuration/rest-configuration.interface';
import {
  EmployeeFingerprintEnrollmentDialogComponent,
  FingerprintEnrollmentDialogResult,
} from './employee-fingerprint-enrollment-dialog.component';
import { OrgUnitPickerDialogComponent, OrgUnitPickerNode } from './org-unit-picker-dialog.component';
import { EmployeeTemporaryCredentialsDialogComponent } from './employee-temporary-credentials-dialog.component';
import { UserPasswordResetResponse } from '../../config/users/user.interface';

type DetailMode = 'create' | 'view' | 'edit';
type EmployeeDateTarget = 'hireDate' | 'terminationDate';
type WorkAssignmentDateTarget = 'cycleStartDate' | 'effectiveFrom' | 'effectiveTo';

interface CompanyRecord {
  companiesRecordId: string;
  name: string;
}

interface CompaniesResponse {
  data?: CompanyRecord[];
  items?: CompanyRecord[];
  results?: CompanyRecord[];
  companiesRecords?: CompanyRecord[];
}

interface OrgUnitRecord {
  orgUnitId: string;
  companiesRecordId: string;
  name: string;
  type?: string;
  parentId?: string | null;
  level?: number | null;
  isActive?: boolean;
}

interface OrgUnitsResponse {
  data?: OrgUnitRecord[];
  items?: OrgUnitRecord[];
  results?: OrgUnitRecord[];
  orgUnits?: OrgUnitRecord[];
}

interface EffectiveOperationalConfigResponse {
  workAssignment: {
    configFrom: 'employee' | 'orgUnit' | 'companiesRecord' | 'defaults';
    configFromId?: string | null;
    value: EmployeeWorkAssignment | null;
  };
  attendanceConfig: {
    configFrom: 'employee' | 'orgUnit' | 'companiesRecord' | 'center' | 'defaults';
    configFromId?: string | null;
    attendanceConfigId?: string | null;
    value: Omit<AttendanceConfigRecord, 'attendanceConfigId'> & { attendanceConfigId?: string };
  };
}

interface EmployeeFormValue {
  employeeNumber: string;
  firstName: string;
  lastName: string;
  secondLastName: string;
  email: string;
  hireDate: string;
  terminationDate: string;
  companiesRecordId: string;
  orgUnitId: string;
  status: string;
}

interface WorkAssignmentFormValue {
  enabled: boolean;
  workCycleId: string;
  restConfigurationId: string;
  cycleStartDate: string;
  effectiveFrom: string;
  effectiveTo: string;
  toleranceRules: {
    entryToleranceMinutes: number | '';
    earlyExitToleranceMinutes: number | '';
    roundingMarginMinutes: number | '';
  };
  overtimePolicy: {
    ignoreOvertime: boolean;
  };
  turnPolicy: {
    enableAutoClose: boolean;
    autoCloseGraceMinutes: number | '';
  };
}

interface AssignmentCalendarDay {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isStart: boolean;
  isEnd: boolean;
  isInRange: boolean;
  isToday: boolean;
}

interface AssignmentCalendarMonth {
  label: string;
  days: Array<AssignmentCalendarDay | null>;
}

interface AssignmentScheduleCalendarDay extends AssignmentCalendarDay {
  previewDay: WorkCyclePreviewDay | null;
  weekdayLabel: string;
}

interface AssignmentScheduleCalendarMonth {
  label: string;
  days: AssignmentScheduleCalendarDay[];
}

interface WorkCyclePreviewDay {
  dayOrder: number;
  dayType: 'work' | 'rest';
  scheduleName: string;
  scheduleRange: string;
  expectedHours: string;
  isRest: boolean;
}

interface WorkAssignmentAppliedPreviewDay extends WorkCyclePreviewDay {
  appliedDate: Date;
  dateLabel: string;
  weekdayLabel: string;
}

@Component({
  selector: 'app-employee-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    CompanySelectComponent,
    LoadingStateComponent,
    DatePickerModule,
    IftaLabelModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTabsModule,
    MatTooltipModule,
    TranslateModule,
  ],
  templateUrl: './employee-detail.component.html',
  styleUrl: './employee-detail.component.scss',
})
export class EmployeeDetailComponent implements OnInit {
  @ViewChildren(DatePicker) private datePickers!: QueryList<DatePicker>;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly appConfigService = inject(AppConfigService);
  private readonly companySelection = inject(CompanySelectionService);

  isLoading = true;
  mode: DetailMode = 'view';
  employeeId: string | null = null;
  selectedTabIndex = 0;
  assignmentScheduleMonthIndex = 0;
  employee: Partial<EmployeeRecord> = {
    employeeNumber: '',
    firstName: '',
    lastName: '',
    secondLastName: '',
    fullName: '',
    email: '',
    hireDate: '',
    terminationDate: '',
    companiesRecordId: '',
    orgUnitId: '',
    status: 'active',
    isActive: true,
  };
  entityForm: EmployeeFormValue = this.createEmptyEntityForm();
  workAssignmentForm: WorkAssignmentFormValue = this.createEmptyWorkAssignmentForm();
  entityDatePickerValues: Record<'hireDate' | 'terminationDate', Date | null> = {
    hireDate: null,
    terminationDate: null,
  };
  workAssignmentDatePickerValues: Record<'cycleStartDate' | 'effectiveFrom' | 'effectiveTo', Date | null> = {
    cycleStartDate: null,
    effectiveFrom: null,
    effectiveTo: null,
  };
  workAssignment: EmployeeWorkAssignment | null = null;
  companies: CompanyRecord[] = [];
  orgUnits: OrgUnitRecord[] = [];
  orgUnitTree: OrgUnitPickerNode[] = [];
  workCycles: WorkCycleConfig[] = [];
  selectedWorkCycleDetail: WorkCycleConfig | null = null;
  dailySchedules: DailyScheduleConfig[] = [];
  restConfigurations: RestConfigurationConfig[] = [];
  devices: DeviceRecord[] = [];
  selectedDeviceIds: string[] = [];
  attendanceConfig: AttendanceConfigRecord | null = null;
  attendanceConfigForm: AttendanceConfigFormValue = createEmptyAttendanceConfigForm();
  zoneOptions: AttendanceZoneOption[] = [];
  biometricTemplates: EmployeeBiometricTemplateRecord[] = [];
  mobileFaceProfile: EmployeeFaceProfileRecord | null = null;
  isLoadingMobileFaceProfile = false;
  isSavingMobileFaceProfile = false;
  isDeletingMobileFaceProfile = false;
  isMobileFaceFormVisible = false;
  mobileFaceProfileForm = {
    embeddingText: '',
    embeddingSize: 128,
    sampleCount: 1,
    threshold: 0.8,
  };
  effectiveConfig: EffectiveOperationalConfigResponse | null = null;
  isLoadingEffectiveConfig = false;
  effectiveConfigError = '';
  emailServerError = '';
  private effectiveConfigRequestId = 0;
  private editableStateBaseline = '';
  private bypassUnsavedChangesGuard = false;
  readonly attendanceSourceOptions = [...ATTENDANCE_ALLOWED_SOURCE_OPTIONS];
  readonly attendanceSourceRuleOptions = [...ATTENDANCE_SOURCE_OVERRIDE_OPTIONS];
  readonly attendanceTimezoneOptions = [...ATTENDANCE_TIMEZONE_OPTIONS];
  readonly attendanceTrackingIntervalOptions = [...ATTENDANCE_TRACKING_INTERVAL_PRESET_SECONDS];
  readonly getAttendanceSourceLabelKey = getAttendanceSourceLabelKey;

  async ngOnInit(): Promise<void> {
    this.resolveMode();
    await this.loadCompanies();
    await this.loadData();
    this.captureEditableStateBaseline();
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get canEdit(): boolean {
    return this.authorizationService.hasAccess({ resource: 'employees', action: 'update' });
  }

  get canResetPassword(): boolean {
    return (
      !this.isCreateMode &&
      !!this.employee.userUid &&
      this.authorizationService.hasAccess({ resource: 'users', action: 'reset-password' })
    );
  }

  get canReadAttendanceConfig(): boolean {
    return (
      this.authorizationService.hasAccess({ resource: 'attendanceConfigs', action: 'read' }) ||
      this.authorizationService.hasAccess({ resource: 'attendanceConfigs', action: 'access' })
    );
  }

  get canManageAttendanceConfig(): boolean {
    return (
      this.authorizationService.hasAccess({ resource: 'attendanceConfigs', action: 'create' }) ||
      this.authorizationService.hasAccess({ resource: 'attendanceConfigs', action: 'update' }) ||
      this.authorizationService.hasAccess({ resource: 'attendanceConfigs', action: 'delete' })
    );
  }

  get pageTitle(): string {
    if (this.mode === 'create') {
      return this.t('employeesCatalog.dialog.createTitle');
    }
    if (this.mode === 'edit') {
      return this.t('employeesCatalog.dialog.editTitle');
    }
    return this.employee.fullName || this.employee.employeeNumber || this.t('employeesCatalog.title');
  }

  get employeeDisplayName(): string {
    const parts = [
      this.entityForm.firstName,
      this.entityForm.lastName,
      this.entityForm.secondLastName,
    ]
      .map((value) => (value || '').trim())
      .filter(Boolean);
    return parts.join(' ') || this.employee.fullName || this.entityForm.employeeNumber || '-';
  }

  get employeeInitials(): string {
    const tokens = this.employeeDisplayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2);
    return tokens.map((token) => token.charAt(0).toUpperCase()).join('') || 'EM';
  }

  get employeeStatusLabel(): string {
    return this.entityForm.status === 'inactive'
      ? this.t('common.status.inactive')
      : this.t('common.status.active');
  }

  get employeeNumberingMode(): 'manual' | 'auto' | 'hybrid' {
    const mode = this.appConfigService.getCenterConfig()?.employeeNumbering?.mode;
    return mode === 'manual' || mode === 'auto' || mode === 'hybrid' ? mode : 'manual';
  }

  get shouldShowEmployeeNumberField(): boolean {
    return !this.isCreateMode || this.employeeNumberingMode !== 'auto';
  }

  get isEmployeeNumberRequired(): boolean {
    if (!this.isCreateMode) {
      return true;
    }
    return this.employeeNumberingMode === 'manual';
  }

  get shouldShowEmployeeNumberAutoTooltip(): boolean {
    return this.isCreateMode && this.employeeNumberingMode === 'hybrid' && !this.isViewMode;
  }

  get shouldShowEmployeeNumberHelp(): boolean {
    return this.isCreateMode && this.shouldShowEmployeeNumberField && !this.isViewMode;
  }

  get employeeNumberPadLength(): number {
    const padLength = Number(this.appConfigService.getCenterConfig()?.employeeNumbering?.padLength || 0);
    return Number.isFinite(padLength) && padLength > 0 ? padLength : 0;
  }

  get employeeNumberHelpHint(): string {
    if (
      !this.shouldShowEmployeeNumberHelp ||
      (this.employeeNumberingMode === 'manual' && !this.employeeNumberPadLength)
    ) {
      return '';
    }

    const key = this.employeeNumberingMode === 'manual'
      ? 'employeesCatalog.fields.employeeNumberManualHint'
      : 'employeesCatalog.fields.employeeNumberHybridHint';
    return this.t(key, { length: this.employeeNumberPadLength });
  }

  get employeeNumberHelpTooltip(): string {
    if (
      !this.shouldShowEmployeeNumberHelp ||
      (this.employeeNumberingMode === 'manual' && !this.employeeNumberPadLength)
    ) {
      return '';
    }

    const key = this.employeeNumberingMode === 'manual'
      ? 'employeesCatalog.fields.employeeNumberManualTooltip'
      : 'employeesCatalog.fields.employeeNumberHybridTooltip';
    return this.t(key, { length: this.employeeNumberPadLength });
  }

  get hasInvalidEmployeeNumberLength(): boolean {
    if (
      !this.isCreateMode ||
      this.employeeNumberingMode !== 'manual' ||
      !this.shouldShowEmployeeNumberField ||
      !this.employeeNumberPadLength
    ) {
      return false;
    }

    const employeeNumber = this.entityForm.employeeNumber.trim();
    if (!employeeNumber) {
      return false;
    }

    return employeeNumber.length !== this.employeeNumberPadLength;
  }

  get hasInvalidEmail(): boolean {
    const email = this.entityForm.email.trim();
    return !!email && !this.isValidEmail(email);
  }

  get requiresMobileUser(): boolean {
    if (!this.isCreateMode) {
      return false;
    }

    const allowedSources = this.canManageAttendanceConfig && this.attendanceConfigForm.enabled
      ? this.attendanceConfigForm.allowedSources
      : this.effectiveConfig?.attendanceConfig.value.attendanceCapture.allowedSources || [];
    return allowedSources.includes('mobile');
  }

  get isEmailRequired(): boolean {
    return this.requiresMobileUser;
  }

  get isEmployeeSaveDisabled(): boolean {
    return this.isLoading || this.isLoadingEffectiveConfig || !!this.validateForm();
  }

  get hasUnsavedChanges(): boolean {
    if (this.isViewMode || this.bypassUnsavedChangesGuard || !this.editableStateBaseline) {
      return false;
    }

    return this.serializeEditableState() !== this.editableStateBaseline;
  }

  get shouldShowLoginSection(): boolean {
    if (!this.isCreateMode) {
      return !!this.entityForm.email.trim();
    }

    return this.isLoadingEffectiveConfig || this.requiresMobileUser || !!this.effectiveConfigError;
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedChanges) {
      return;
    }

    event.preventDefault();
    event.returnValue = '';
  }

  @HostListener('document:mousedown', ['$event'])
  closeDatePickersOnOutsideClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const clickedDatePicker = target.closest('p-datepicker, .app-date-picker-panel');
    if (clickedDatePicker) {
      return;
    }

    this.datePickers?.forEach((datePicker) => datePicker.hideOverlay());
  }

  get displayedWorkAssignment(): EmployeeWorkAssignment | null {
    return this.workAssignmentForm.enabled
      ? this.workAssignment
      : this.effectiveConfig?.workAssignment.value || null;
  }

  get displayedAttendanceConfig(): AttendanceConfigRecord | null {
    if (this.attendanceConfig && this.attendanceConfigForm.enabled) {
      return this.attendanceConfig;
    }
    const effective = this.effectiveConfig?.attendanceConfig;
    if (!effective?.value) {
      return null;
    }
    return {
      ...effective.value,
      attendanceConfigId: effective.attendanceConfigId || effective.value.attendanceConfigId || '',
    };
  }

  get workAssignmentOriginLabel(): string {
    if (this.workAssignmentForm.enabled) {
      return this.t('operationalConfigInheritance.own.employee');
    }
    return this.buildEffectiveOriginLabel(this.effectiveConfig?.workAssignment);
  }

  get attendanceConfigOriginLabel(): string {
    if (this.attendanceConfigForm.enabled) {
      return this.t('operationalConfigInheritance.own.employee');
    }
    return this.buildEffectiveOriginLabel(this.effectiveConfig?.attendanceConfig);
  }

  get breadcrumbItems(): string[] {
    const base = [this.t('employeesCatalog.breadcrumb')];
    if (this.mode === 'create') {
      return [...base, this.t('employeesCatalog.actions.create')];
    }
    if (this.mode === 'edit') {
      return [...base, this.employee.fullName || this.t('employeesCatalog.actions.edit')];
    }
    return [...base, this.employee.fullName || this.t('employeesCatalog.actions.view')];
  }

  get selectedWorkCycleName(): string {
    if (!this.workAssignmentForm.workCycleId) {
      return '-';
    }
    return this.workCycles.find((cycle) => cycle.workCycleId === this.workAssignmentForm.workCycleId)?.name || '-';
  }

  get selectedRestConfigurationName(): string {
    if (!this.workAssignmentForm.restConfigurationId) {
      return '-';
    }
    return (
      this.restConfigurations.find(
        (restConfiguration) => restConfiguration.restConfigurationId === this.workAssignmentForm.restConfigurationId
      )?.name || '-'
    );
  }

  get workAssignmentStatusLabel(): string {
    return this.workAssignmentForm.enabled
      ? this.t('common.status.active')
      : this.t('common.status.inactive');
  }

  get workAssignmentValidityLabel(): string {
    const from = this.workAssignmentForm.effectiveFrom || '-';
    const to = this.workAssignmentForm.effectiveTo || this.t('employeesCatalog.workAssignment.openEnded');
    return `${from} - ${to}`;
  }

  get workAssignmentAnchorSummary(): string {
    return this.workAssignmentForm.cycleStartDate || '-';
  }

  get assignmentCyclePreviewDays(): WorkCyclePreviewDay[] {
    const cycle = this.selectedWorkCycleDetail;
    if (!cycle?.days?.length) {
      return [];
    }

    return [...cycle.days]
      .sort((left, right) => Number(left.position || 0) - Number(right.position || 0))
      .map((day, index) => {
        const schedule = day.dailyScheduleId
          ? this.dailySchedules.find((item) => item.dailyScheduleId === day.dailyScheduleId) || null
          : null;
        const isRest = day.dayType === 'rest' || !schedule;

        return {
          dayOrder: Number(day.position || index + 1),
          dayType: isRest ? 'rest' : 'work',
          scheduleName: isRest ? this.t('employeesCatalog.workAssignment.cyclePreview.rest') : schedule?.name || '-',
          scheduleRange: isRest ? this.t('employeesCatalog.workAssignment.cyclePreview.noHours') : this.getDailyScheduleRangeLabel(schedule),
          expectedHours: isRest ? this.t('employeesCatalog.workAssignment.cyclePreview.noHours') : this.getDailyScheduleHoursLabel(schedule),
          isRest,
        };
      });
  }

  get assignmentAppliedPreviewDays(): WorkAssignmentAppliedPreviewDay[] {
    const cycle = this.selectedWorkCycleDetail;
    const referenceDate = this.getAssignmentCyclePreviewReferenceDate();
    const cycleDays = this.assignmentCyclePreviewDays;

    if (!cycle || !referenceDate || !cycleDays.length) {
      return [];
    }

    const locale = this.getCalendarLocale();
    const weekdayFormatter = new Intl.DateTimeFormat(locale, { weekday: 'long' });
    const dateFormatter = new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
    });

    return Array.from({ length: cycle.cycleLength }, (_, index) => {
      const appliedDate = this.addDays(referenceDate, index);
      const cycleDay = this.getAppliedCycleDayForDate(appliedDate, cycleDays);
      const weekdayLabel = weekdayFormatter.format(appliedDate);
      return {
        ...cycleDay,
        appliedDate,
        dateLabel: dateFormatter.format(appliedDate),
        weekdayLabel: weekdayLabel.charAt(0).toUpperCase() + weekdayLabel.slice(1),
      };
    });
  }

  get assignmentAppliedPreviewWeeks(): Array<Array<WorkAssignmentAppliedPreviewDay | null>> {
    const days = this.assignmentAppliedPreviewDays;
    if (!days.length) {
      return [];
    }

    const firstDate = days[0].appliedDate;
    const lastDate = days[days.length - 1].appliedDate;
    const gridStart = this.startOfWeek(firstDate);
    const gridEnd = this.endOfWeek(lastDate);
    const dayMap = new Map(
      days.map((day) => [this.toIsoDateString(day.appliedDate), day] as const)
    );

    const weeks: Array<Array<WorkAssignmentAppliedPreviewDay | null>> = [];
    let currentWeek: Array<WorkAssignmentAppliedPreviewDay | null> = [];

    for (let cursor = new Date(gridStart); cursor <= gridEnd; cursor = this.addDays(cursor, 1)) {
      currentWeek.push(dayMap.get(this.toIsoDateString(cursor)) || null);

      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    if (currentWeek.length) {
      weeks.push(currentWeek);
    }

    return weeks;
  }

  get assignmentScheduleMonthStarts(): Date[] {
    const start = this.getAssignmentCyclePreviewReferenceDate();
    if (!start) {
      return [];
    }

    const end = this.parseLocalDate(this.workAssignmentForm.effectiveTo);
    if (!end) {
      return Array.from({ length: this.assignmentScheduleMonthIndex + 1 }, (_, index) => {
        return new Date(start.getFullYear(), start.getMonth() + index, 1);
      });
    }

    const months: Date[] = [];
    let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const lastMonth = new Date(end.getFullYear(), end.getMonth(), 1);

    while (cursor <= lastMonth) {
      months.push(new Date(cursor));
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }

    return months;
  }

  get hasPreviousAssignmentScheduleMonth(): boolean {
    return this.assignmentScheduleMonthIndex > 0;
  }

  get hasNextAssignmentScheduleMonth(): boolean {
    if (!this.parseLocalDate(this.workAssignmentForm.effectiveTo) && !!this.getAssignmentCyclePreviewReferenceDate()) {
      return true;
    }
    return this.assignmentScheduleMonthIndex < this.assignmentScheduleMonthStarts.length - 1;
  }

  get assignmentSelectedScheduleMonth(): AssignmentScheduleCalendarMonth | null {
    const start = this.getAssignmentCyclePreviewReferenceDate();
    const end = this.parseLocalDate(this.workAssignmentForm.effectiveTo);
    const cycleDays = this.assignmentCyclePreviewDays;

    if (!start || !cycleDays.length) {
      return null;
    }

    const monthStarts = this.assignmentScheduleMonthStarts;
    const index = monthStarts.length
      ? Math.min(Math.max(this.assignmentScheduleMonthIndex, 0), monthStarts.length - 1)
      : 0;
    const monthDate = end
      ? monthStarts[index]
      : new Date(start.getFullYear(), start.getMonth() + Math.max(this.assignmentScheduleMonthIndex, 0), 1);

    return this.buildAssignmentScheduleCalendarMonth(monthDate, start, end, cycleDays);
  }

  get attendanceTrackingIntervalLabel(): string {
    const tracking = this.displayedAttendanceConfig?.tracking as
      | { trackingIntervalSeconds?: number; trackingIntervalMinutes?: number }
      | undefined;
    if (tracking?.trackingIntervalSeconds) {
      return formatAttendanceTrackingInterval(tracking.trackingIntervalSeconds);
    }
    if (tracking?.trackingIntervalMinutes) {
      return formatAttendanceTrackingInterval(undefined, tracking.trackingIntervalMinutes);
    }
    return formatAttendanceTrackingInterval(this.attendanceConfigForm.trackingIntervalSeconds || 0);
  }

  get selectedCompanyName(): string {
    return this.companies.find((company) => company.companiesRecordId === this.entityForm.companiesRecordId)?.name || '-';
  }

  get selectedOrgUnitName(): string {
    return this.findOrgUnitNodeById(this.orgUnitTree, this.entityForm.orgUnitId)?.name || '';
  }

  get selectedOrgUnitPath(): string {
    const node = this.findOrgUnitNodeById(this.orgUnitTree, this.entityForm.orgUnitId);
    return node?.pathLabels.join(' / ') || '';
  }

  get assignmentPreviewSummary(): string {
    if (!this.workAssignmentForm.enabled) {
      return this.t('employeesCatalog.workAssignment.previewDisabled');
    }

    const from = this.workAssignmentForm.effectiveFrom || '-';
    const to = this.workAssignmentForm.effectiveTo || this.t('employeesCatalog.workAssignment.openEnded');
    return this.t('employeesCatalog.workAssignment.previewSummary', { from, to });
  }

  get assignmentPreviewDuration(): string {
    const start = this.parseLocalDate(this.workAssignmentForm.effectiveFrom);
    const end = this.parseLocalDate(this.workAssignmentForm.effectiveTo);

    if (!start || !end || end < start) {
      return this.t('employeesCatalog.workAssignment.previewDurationUnknown');
    }

    const days = Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
    return this.t('employeesCatalog.workAssignment.previewDuration', { days });
  }

  get assignmentPreviewMonths(): AssignmentCalendarMonth[] {
    const start = this.parseLocalDate(this.workAssignmentForm.effectiveFrom);
    if (!start) {
      return [];
    }

    const end = this.parseLocalDate(this.workAssignmentForm.effectiveTo) || start;
    const firstMonth = new Date(start.getFullYear(), start.getMonth(), 1);
    const secondMonth =
      start.getFullYear() !== end.getFullYear() || start.getMonth() !== end.getMonth()
        ? new Date(end.getFullYear(), end.getMonth(), 1)
        : null;

    return [firstMonth, secondMonth]
      .filter((month): month is Date => !!month)
      .map((month) => this.buildAssignmentCalendarMonth(month, start, end));
  }

  get assignmentWeekdayLabels(): string[] {
    return this.translate.currentLang?.startsWith('en')
      ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
      : ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  }

  get canSelectOrgUnit(): boolean {
    return !this.isViewMode && !!this.entityForm.companiesRecordId;
  }

  get isOrganizationComplete(): boolean {
    return !!this.entityForm.companiesRecordId?.trim() && !!this.entityForm.orgUnitId?.trim();
  }

  get canEnrollFingerprint(): boolean {
    return (
      !this.isCreateMode &&
      !!this.entityForm.employeeNumber.trim() &&
      this.devices.some((device) => device.isActive !== false) &&
      this.authorizationService.hasAccess({ resource: 'devices', action: 'update' })
    );
  }

  get fingerprintTemplates(): EmployeeBiometricTemplateRecord[] {
    return this.biometricTemplates.filter((template) => template.modality === 'fingerprint' && template.isActive !== false);
  }

  get faceTemplates(): EmployeeBiometricTemplateRecord[] {
    return this.biometricTemplates.filter((template) => template.modality === 'face' && template.isActive !== false);
  }

  get activeBiometricTemplates(): EmployeeBiometricTemplateRecord[] {
    return this.biometricTemplates.filter((template) => template.isActive !== false);
  }

  get hasMobileFaceProfile(): boolean {
    return !!this.mobileFaceProfile;
  }

  get assignedDevices(): DeviceRecord[] {
    if (!this.selectedDeviceIds.length) {
      return [];
    }
    return this.devices.filter((device) => this.selectedDeviceIds.includes(device.deviceId));
  }

  get assignedDeviceCount(): number {
    return this.assignedDevices.length;
  }

  get availableDeviceCount(): number {
    return this.devices.length;
  }

  get attendanceConfigSourcesLabel(): string {
    const sources = this.displayedAttendanceConfig?.attendanceCapture?.allowedSources || [];
    return sources.length ? sources.map((source) => this.t(getAttendanceSourceLabelKey(source))).join(', ') : '-';
  }

  get attendanceConfigZonesLabel(): string {
    const zoneIds = this.displayedAttendanceConfig?.zones?.allowedZoneIds || [];
    if (!zoneIds.length) {
      return '-';
    }

    return zoneIds
      .map((zoneId) => this.zoneOptions.find((zone) => zone.zoneId === zoneId)?.name || zoneId)
      .join(', ');
  }

  get attendanceConfigFormSourcesLabel(): string {
    return this.attendanceConfigForm.allowedSources.length
      ? this.attendanceConfigForm.allowedSources.map((source) => this.t(getAttendanceSourceLabelKey(source))).join(', ')
      : '-';
  }

  get attendanceConfigFormZonesLabel(): string {
    const zoneIds = this.attendanceConfigForm.allowedZoneIds || [];
    if (!zoneIds.length) {
      return '-';
    }

    return zoneIds
      .map((zoneId) => this.zoneOptions.find((zone) => zone.zoneId === zoneId)?.name || zoneId)
      .join(', ');
  }

  get hasAttendanceConfigSourceOverrides(): boolean {
    return this.attendanceSourceRuleOptions.some((source) => !!this.getAttendanceConfigSourceRule(source));
  }

  getAttendanceConfigSourceRule(source: string): AttendanceSourceRuleConfig | null {
    return this.displayedAttendanceConfig?.sourceRules?.[source] ?? null;
  }

  async save(): Promise<void> {
    if (this.isViewMode) {
      return;
    }

    if (this.isCreateMode && this.entityForm.orgUnitId) {
      await this.loadEffectiveConfig('orgUnit', this.entityForm.orgUnitId);
    }

    const validationMessage = this.validateForm();
    if (validationMessage) {
      this.showNotification('snackbar-warning', validationMessage, 'bottom', 'center');
      return;
    }

    this.isLoading = true;
    try {
      const payload = this.buildPayload();
      if (this.isCreateMode) {
        const attendanceConfigPayload = this.buildCreateAttendanceConfigPayload();
        const created = await firstValueFrom(
          this.apiService.post<EmployeeWithLaborConfigResponse>('employees/with-operational-config', {
            ...payload,
            ...(attendanceConfigPayload ? { attendanceConfig: attendanceConfigPayload } : {}),
          })
        );

        this.showNotification('snackbar-success', this.t('employeesCatalog.notifications.created'), 'bottom', 'center');
        this.bypassUnsavedChangesGuard = true;
        await this.showTemporaryCredentials(created.entity.userProvisioning);
        await this.router.navigate(['/organization/employees']);
        return;
      }

      await firstValueFrom(
        this.apiService.patch<EmployeeWithLaborConfigResponse>(`employees/${this.employeeId}/with-operational-config`, payload)
      );
      await this.persistAttendanceConfig();
      this.showNotification('snackbar-success', this.t('employeesCatalog.notifications.updated'), 'bottom', 'center');
      this.bypassUnsavedChangesGuard = true;
      await this.router.navigate(['/organization/employees']);
    } catch (error) {
      console.error('Error saving employee', error);
      const errorMessage = extractApiErrorMessage(
        error,
        this.isCreateMode ? this.t('employeesCatalog.notifications.createError') : this.t('employeesCatalog.notifications.updateError')
      );
      if (this.isCreateMode && /email|correo/i.test(errorMessage)) {
        this.emailServerError = errorMessage;
        this.selectedTabIndex = 0;
      }
      this.showNotification(
        'snackbar-danger',
        errorMessage,
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  async goToEdit(): Promise<void> {
    if (!this.employeeId || !this.canEdit) {
      return;
    }
    await this.router.navigate(['/organization/employees', this.employeeId, 'edit']);
  }

  async goBack(): Promise<void> {
    await this.router.navigate(['/organization/employees']);
  }

  async resetLinkedUserPassword(): Promise<void> {
    const userUid = this.employee.userUid;
    if (!userUid || !this.canResetPassword) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '28rem',
      maxWidth: '94vw',
      data: {
        title: this.t('employeesCatalog.dialog.resetPasswordTitle'),
        message: this.t('employeesCatalog.dialog.resetPasswordMessage', {
          employee: this.employeeDisplayName,
        }),
        confirmLabel: this.t('employeesCatalog.dialog.resetPasswordConfirm'),
        cancelLabel: this.t('common.cancel'),
        confirmIcon: 'password',
      },
      autoFocus: false,
    });

    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }

    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.patch<UserPasswordResetResponse>(
          `users/${encodeURIComponent(userUid)}/password`,
          null
        )
      );

      if (!response.updated || !response.temporaryPassword) {
        throw new Error(this.t('employeesCatalog.notifications.resetPasswordInvalidResponse'));
      }

      const credentialsRef = this.dialog.open(EmployeeTemporaryCredentialsDialogComponent, {
        width: '560px',
        maxWidth: '95vw',
        disableClose: true,
        autoFocus: false,
        data: {
          title: this.t('employeesCatalog.passwordReset.title'),
          description: this.t('employeesCatalog.passwordReset.description'),
          account: this.entityForm.email.trim() || response.uid,
          accountLabel: this.entityForm.email.trim()
            ? this.t('employeesCatalog.fields.email')
            : this.t('usersCatalog.fields.uid'),
          password: response.temporaryPassword,
          mustChangePassword: response.mustChangePassword === true,
        },
      });
      await firstValueFrom(credentialsRef.afterClosed());
      this.showNotification(
        'snackbar-success',
        this.t('employeesCatalog.notifications.passwordReset'),
        'bottom',
        'center'
      );
    } catch (error) {
      console.error('Error resetting employee user password', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('employeesCatalog.notifications.resetPasswordError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  openFingerprintEnrollmentDialog(): void {
    const employeeNumber = this.entityForm.employeeNumber.trim();
    if (!employeeNumber) {
      this.showNotification('snackbar-warning', this.t('employeesCatalog.notifications.validationFingerprintEmployeeNumber'), 'bottom', 'center');
      return;
    }

    const activeDevices = this.devices.filter((device) => device.isActive !== false);
    const scopedDevices = this.selectedDeviceIds.length
      ? activeDevices.filter((device) => this.selectedDeviceIds.includes(device.deviceId))
      : activeDevices;

    const availableDevices = scopedDevices
      .map((device) => ({
        deviceId: device.deviceId,
        name: device.name || device.serial || '-',
        serial: device.serial,
      }));

    if (!availableDevices.length) {
      this.showNotification('snackbar-warning', this.t('employeesCatalog.notifications.validationFingerprintDevice'), 'bottom', 'center');
      return;
    }

    const dialogRef = this.dialog.open(EmployeeFingerprintEnrollmentDialogComponent, {
      width: '980px',
      maxWidth: '96vw',
      data: {
        employeeId: this.employee.employeeId,
        employeeName: this.employee.fullName || `${this.entityForm.firstName} ${this.entityForm.lastName}`.trim() || employeeNumber,
        employeeNumber,
        devices: availableDevices,
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(async (result: FingerprintEnrollmentDialogResult | undefined) => {
      if (!result) {
        return;
      }

      await this.queueFingerprintEnrollment(result);
    });
  }

  async onCompanyChange(): Promise<void> {
    this.companySelection.rememberSelection(this.entityForm.companiesRecordId);
    this.resetEffectiveConfig();
    this.entityForm.orgUnitId = '';
    this.workAssignmentForm = this.createEmptyWorkAssignmentForm();
    this.syncWorkAssignmentDatePickerValues();
    this.selectedWorkCycleDetail = null;
    this.assignmentScheduleMonthIndex = 0;
    await Promise.all([
      this.loadOrgUnits(this.entityForm.companiesRecordId),
      this.loadWorkCycles(this.entityForm.companiesRecordId),
      this.loadDailySchedules(this.entityForm.companiesRecordId),
      this.loadRestConfigurations(this.entityForm.companiesRecordId),
      this.loadDevices(this.entityForm.companiesRecordId),
      this.loadZones(this.entityForm.companiesRecordId),
    ]);
    this.attendanceConfigForm.allowedZoneIds = this.attendanceConfigForm.allowedZoneIds.filter((zoneId) =>
      this.zoneOptions.some((zone) => zone.zoneId === zoneId)
    );
    if (this.isCreateMode && this.entityForm.companiesRecordId) {
      await this.loadEffectiveConfig('companiesRecord', this.entityForm.companiesRecordId);
    }
    this.syncWorkAssignmentDatePickerValues();
  }

  openOrgUnitPicker(): void {
    if (this.isViewMode || !this.entityForm.companiesRecordId || !this.orgUnitTree.length) {
      return;
    }

    const dialogRef = this.dialog.open(OrgUnitPickerDialogComponent, {
      width: '920px',
      maxWidth: '95vw',
      data: {
        nodes: this.orgUnitTree,
        selectedOrgUnitId: this.entityForm.orgUnitId,
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((selectedNode: OrgUnitPickerNode | null | undefined) => {
      if (selectedNode === undefined) {
        return;
      }

      this.entityForm.orgUnitId = selectedNode?.orgUnitId || '';
      this.resetEffectiveConfig();
      if (this.isCreateMode && this.entityForm.orgUnitId) {
        void this.loadEffectiveConfig('orgUnit', this.entityForm.orgUnitId);
      }
    });
  }

  onEmailChange(): void {
    this.emailServerError = '';
  }

  onWorkAssignmentToggle(): void {
    if (!this.workAssignmentForm.enabled) {
      this.assignmentScheduleMonthIndex = 0;
      void this.restoreInheritedEffectiveConfig();
    }
  }

  async onWorkCycleChange(): Promise<void> {
    await this.loadSelectedWorkCycleDetail(this.workAssignmentForm.workCycleId);
    this.assignmentScheduleMonthIndex = 0;
  }

  onWorkAssignmentDatesChange(): void {
    this.assignmentScheduleMonthIndex = 0;
  }

  showPreviousAssignmentScheduleMonth(): void {
    if (this.hasPreviousAssignmentScheduleMonth) {
      this.assignmentScheduleMonthIndex -= 1;
    }
  }

  showNextAssignmentScheduleMonth(): void {
    if (this.hasNextAssignmentScheduleMonth) {
      this.assignmentScheduleMonthIndex += 1;
    }
  }

  openDatePicker(input: HTMLInputElement): void {
    const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof pickerInput.showPicker === 'function') {
      pickerInput.showPicker();
      return;
    }

    input.focus();
    input.click();
  }

  toPickerDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split('-').map((part) => Number(part));
    if (!year || !month || !day) {
      return null;
    }

    return new Date(year, month - 1, day);
  }

  fromPickerDate(value: Date | null | undefined): string {
    if (!value || Number.isNaN(value.getTime())) {
      return '';
    }

    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onEmployeeDateChange(
    target: EmployeeDateTarget,
    value: Date | null
  ): void {
    this.entityDatePickerValues[target] = value;
    this.entityForm[target] = this.fromPickerDate(value);
  }

  onWorkAssignmentPickerDateChange(
    target: WorkAssignmentDateTarget,
    value: Date | null
  ): void {
    this.workAssignmentDatePickerValues[target] = value;
    this.updateWorkAssignmentDate(target, this.fromPickerDate(value));
    this.syncWorkAssignmentDatePickerValues();
  }

  onManualEmployeeDateInput(target: EmployeeDateTarget, event: Event): void {
    const parsed = this.parseCompactDateInput(event);
    if (parsed) {
      this.onEmployeeDateChange(target, parsed.date);
      parsed.input.value = parsed.displayValue;
    }
  }

  onManualWorkAssignmentDateInput(target: WorkAssignmentDateTarget, event: Event): void {
    const parsed = this.parseCompactDateInput(event);
    if (parsed) {
      this.onWorkAssignmentPickerDateChange(target, parsed.date);
      parsed.input.value = parsed.displayValue;
    }
  }

  onWorkAssignmentDateChange(
    target: 'cycleStartDate' | 'effectiveFrom' | 'effectiveTo',
    value: string
  ): void {
    this.updateWorkAssignmentDate(target, value);
  }

  onEmployeeNumberChange(value: string): void {
    this.entityForm.employeeNumber =
      this.employeeNumberingMode === 'manual' && this.employeeNumberPadLength > 0
        ? value.slice(0, this.employeeNumberPadLength)
        : value;
  }

  onAttendanceConfigToggle(): void {
    if (!this.attendanceConfigForm.enabled) {
      void this.restoreInheritedEffectiveConfig();
    }
  }

  onAttendanceAllowedSourcesChange(): void {
    for (const source of this.attendanceSourceRuleOptions) {
      if (!this.attendanceConfigForm.allowedSources.includes(source)) {
        this.attendanceConfigForm.sourceRules[source] = createEmptyAttendanceSourceRuleForm(source, this.attendanceConfigForm);
      }
    }
  }

  getAttendanceSourceRuleForm(source: string): AttendanceSourceRuleFormValue {
    return this.attendanceConfigForm.sourceRules[source];
  }

  isAttendanceSourceEnabled(source: string): boolean {
    return this.attendanceConfigForm.allowedSources.includes(source);
  }

  onAttendanceSourceRuleToggle(source: string): void {
    const rule = this.getAttendanceSourceRuleForm(source);
    if (!rule) {
      return;
    }

    if (rule.enabled) {
      this.attendanceConfigForm.sourceRules[source] = {
        ...rule,
        timezone: this.attendanceConfigForm.timezone,
        requireGeo: this.attendanceConfigForm.requireGeo,
        requireFaceEvidence: this.attendanceConfigForm.requireFaceEvidence,
        duplicateWindowSeconds: this.attendanceConfigForm.duplicateWindowSeconds,
        allowUntypedEvents: this.attendanceConfigForm.allowUntypedEvents,
        normalizeUntypedEvents: this.attendanceConfigForm.normalizeUntypedEvents,
        normalizeMistypedEvents: this.attendanceConfigForm.normalizeMistypedEvents,
      };
      return;
    }

    this.attendanceConfigForm.sourceRules[source] = createEmptyAttendanceSourceRuleForm(source, this.attendanceConfigForm);
  }

  attendanceSourceUsesTimezone(source: string): boolean {
    return usesTimezone(source);
  }

  attendanceSourceUsesGeo(source: string): boolean {
    return usesGeo(source);
  }

  attendanceSourceUsesDuplicateWindow(source: string): boolean {
    return usesDuplicateWindow(source);
  }

  attendanceSourceUsesUntypedNormalization(source: string): boolean {
    return usesUntypedNormalization(source);
  }

  attendanceSourceUsesMistypedNormalization(source: string): boolean {
    return usesMistypedNormalization(source);
  }

  attendanceSourceUsesDeviceRules(source: string): boolean {
    return usesDeviceRules(source);
  }

  private resolveMode(): void {
    const currentUrl = this.router.url.split('?')[0].split('#')[0];
    const routeParamId = this.route.snapshot.paramMap.get('employeeId');

    if (currentUrl.endsWith('/new')) {
      this.mode = 'create';
      this.employeeId = null;
      return;
    }

    this.employeeId = routeParamId;
    this.mode = currentUrl.endsWith('/edit') ? 'edit' : 'view';
  }

  private async loadData(): Promise<void> {
    if (this.isCreateMode) {
      this.entityForm = this.createEmptyEntityForm();
      const defaultCompanyId = this.companySelection.resolveSelection(this.companies);
      if (defaultCompanyId) {
        this.entityForm.companiesRecordId = defaultCompanyId;
        await Promise.all([
          this.loadOrgUnits(this.entityForm.companiesRecordId),
          this.loadWorkCycles(this.entityForm.companiesRecordId),
          this.loadDailySchedules(this.entityForm.companiesRecordId),
          this.loadRestConfigurations(this.entityForm.companiesRecordId),
          this.loadDevices(this.entityForm.companiesRecordId),
          this.loadZones(this.entityForm.companiesRecordId),
        ]);
      }
      this.workAssignment = null;
      this.workAssignmentForm = this.createEmptyWorkAssignmentForm();
      this.devices = [];
      this.selectedDeviceIds = [];
      this.attendanceConfig = null;
      this.attendanceConfigForm = createEmptyAttendanceConfigForm();
      this.biometricTemplates = [];
      this.mobileFaceProfile = null;
      this.isMobileFaceFormVisible = false;
      if (this.entityForm.companiesRecordId) {
        await this.loadEffectiveConfig('companiesRecord', this.entityForm.companiesRecordId);
      }
      this.syncDatePickerValues();
      this.isLoading = false;
      return;
    }

    if (!this.employeeId) {
      await this.goBack();
      return;
    }

    this.isLoading = true;
    try {
      const detail = await firstValueFrom(
        this.apiService.get<EmployeeWithLaborConfigResponse>(`employees/${this.employeeId}/with-operational-config`)
      );

      this.employee = detail.entity;
      this.entityForm = {
        employeeNumber: detail.entity.employeeNumber || '',
        firstName: detail.entity.firstName || '',
        lastName: detail.entity.lastName || '',
        secondLastName: detail.entity.secondLastName || '',
        email: detail.entity.email || '',
        hireDate: detail.entity.hireDate || '',
        terminationDate: detail.entity.terminationDate || '',
        companiesRecordId: detail.entity.companiesRecordId || '',
        orgUnitId: detail.entity.orgUnitId || '',
        status: detail.entity.status || 'active',
      };
      await Promise.all([
        this.loadOrgUnits(this.entityForm.companiesRecordId),
        this.loadWorkCycles(this.entityForm.companiesRecordId),
        this.loadDailySchedules(this.entityForm.companiesRecordId),
        this.loadRestConfigurations(this.entityForm.companiesRecordId),
        this.loadDevices(this.entityForm.companiesRecordId),
        this.loadZones(this.entityForm.companiesRecordId),
        this.loadAttendanceConfig(this.employeeId),
        this.loadBiometricTemplates(this.employeeId),
        this.loadMobileFaceProfile(this.employeeId),
      ]);
      this.workAssignment = detail.workAssignment || null;
      this.workAssignmentForm = this.createWorkAssignmentForm(detail.workAssignment || null);
      await this.loadSelectedWorkCycleDetail(this.workAssignmentForm.workCycleId);
      this.selectedDeviceIds = (detail.devices || [])
        .map((device) => device.deviceId)
        .filter((deviceId): deviceId is string => !!deviceId);
      await this.loadEffectiveConfig('employee', this.employeeId);
      this.syncDatePickerValues();
    } catch (error) {
      console.error('Error loading employee detail', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('employeesCatalog.notifications.loadDetailError')),
        'bottom',
        'center'
      );
      await this.goBack();
    } finally {
      this.isLoading = false;
    }
  }

  private async loadCompanies(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiService.get<CompanyRecord[] | CompaniesResponse>('companies-records', {
          params: { isActive: 'true' },
        })
      );
      this.companies = Array.isArray(response)
        ? response
        : response.items || response.data || response.results || response.companiesRecords || [];
    } catch (error) {
      console.error('Error loading companies for employee detail', error);
      this.companies = [];
    }
  }

  private async loadOrgUnits(companyId: string): Promise<void> {
    if (!companyId) {
      this.orgUnits = [];
      this.orgUnitTree = [];
      return;
    }

    try {
      const response = await firstValueFrom(this.apiService.get<OrgUnitRecord[] | OrgUnitsResponse>('org-units'));
      const items = Array.isArray(response) ? response : response.items || response.data || response.results || response.orgUnits || [];
      this.orgUnits = items.filter((item) => item.companiesRecordId === companyId && item.isActive !== false);
      this.orgUnitTree = this.buildOrgUnitTree(this.orgUnits);
    } catch (error) {
      console.error('Error loading org units for employee detail', error);
      this.orgUnits = [];
      this.orgUnitTree = [];
    }
  }

  private async loadWorkCycles(companyId: string): Promise<void> {
    if (!companyId) {
      this.workCycles = [];
      this.selectedWorkCycleDetail = null;
      return;
    }

    try {
      this.workCycles =
        (await firstValueFrom(this.apiService.get<WorkCycleConfig[]>('labor-config/work-cycles', {
          params: { companiesRecordId: companyId },
        }))) || [];
    } catch (error) {
      console.error('Error loading work cycles for employee detail', error);
      this.workCycles = [];
      this.selectedWorkCycleDetail = null;
    }
  }

  private async loadDailySchedules(companyId: string): Promise<void> {
    if (!companyId) {
      this.dailySchedules = [];
      return;
    }

    try {
      this.dailySchedules =
        (
          await firstValueFrom(
            this.apiService.get<DailyScheduleConfig[]>('labor-config/daily-schedules', {
              params: { companiesRecordId: companyId, isActive: 'true' },
            })
          )
        )
          ?.filter((schedule) => schedule.isActive !== false)
          .sort((left, right) => left.name.localeCompare(right.name)) || [];
    } catch (error) {
      console.error('Error loading daily schedules for employee detail', error);
      this.dailySchedules = [];
    }
  }

  private async loadSelectedWorkCycleDetail(workCycleId: string): Promise<void> {
    if (!workCycleId) {
      this.selectedWorkCycleDetail = null;
      return;
    }

    const baseCycle = this.workCycles.find((cycle) => cycle.workCycleId === workCycleId) || null;
    if (baseCycle?.days?.length) {
      this.selectedWorkCycleDetail = baseCycle;
      return;
    }

    try {
      this.selectedWorkCycleDetail = await firstValueFrom(
        this.apiService.get<WorkCycleConfig>(`labor-config/work-cycles/${workCycleId}`)
      );
    } catch (error) {
      console.error('Error loading selected work cycle detail for employee detail', error);
      this.selectedWorkCycleDetail = baseCycle;
    }
  }

  private async loadRestConfigurations(companyId: string): Promise<void> {
    if (!companyId) {
      this.restConfigurations = [];
      return;
    }

    try {
      this.restConfigurations =
        (
          await firstValueFrom(
            this.apiService.get<RestConfigurationConfig[]>('labor-config/rest-configurations', {
              params: { companiesRecordId: companyId, isActive: 'true' },
            })
          )
        )
          ?.filter((restConfiguration) => restConfiguration.isActive !== false)
          .sort((left, right) => left.name.localeCompare(right.name)) || [];

      if (
        this.workAssignmentForm.restConfigurationId &&
        !this.restConfigurations.some(
          (restConfiguration) => restConfiguration.restConfigurationId === this.workAssignmentForm.restConfigurationId
        )
      ) {
        this.workAssignmentForm.restConfigurationId = '';
      }
    } catch (error) {
      console.error('Error loading rest configurations for employee detail', error);
      this.restConfigurations = [];
    }
  }

  private async loadDevices(companyId: string): Promise<void> {
    if (!companyId) {
      this.devices = [];
      this.selectedDeviceIds = [];
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<DevicesResponse>('devices', {
          params: {
            companiesRecordId: companyId,
            perPage: 200,
            page: 1,
          },
        })
      );
      this.devices = (response?.data || []).sort((left, right) =>
        (left.name || left.serial || '').localeCompare(right.name || right.serial || '')
      );
      this.selectedDeviceIds = this.selectedDeviceIds.filter((deviceId) =>
        this.devices.some((device) => device.deviceId === deviceId)
      );
    } catch (error) {
      console.error('Error loading devices for employee detail', error);
      this.devices = [];
      this.selectedDeviceIds = [];
    }
  }

  private async loadZones(companyId: string): Promise<void> {
    if (!companyId) {
      this.zoneOptions = [];
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<AttendanceZoneListResponse>('attendance/zones', {
          params: {
            companiesRecordId: companyId,
            isActive: 'true',
            perPage: 200,
            page: 1,
          },
        })
      );
      this.zoneOptions = extractAttendanceZones(response).filter((zone) => zone.isActive !== false);
    } catch (error) {
      console.error('Error loading attendance zones for employee detail', error);
      this.zoneOptions = [];
    }
  }

  private async loadAttendanceConfig(employeeId?: string | null): Promise<void> {
    if (!employeeId || !this.canReadAttendanceConfig) {
      this.attendanceConfig = null;
      this.attendanceConfigForm = createEmptyAttendanceConfigForm();
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<AttendanceConfigListResponse>('attendance/configs', {
          params: { employeeId },
        })
      );
      this.attendanceConfig = findAttendanceConfigForScope(response, { employeeId });
      this.attendanceConfigForm = mapAttendanceConfigToForm(this.attendanceConfig);
    } catch (error) {
      console.error('Error loading attendance config for employee detail', error);
      this.attendanceConfig = null;
      this.attendanceConfigForm = createEmptyAttendanceConfigForm();
    }
  }

  private resetEffectiveConfig(): void {
    this.effectiveConfigRequestId += 1;
    this.effectiveConfig = null;
    this.effectiveConfigError = '';
    this.isLoadingEffectiveConfig = false;
  }

  private async loadEffectiveConfig(
    elementType: 'companiesRecord' | 'orgUnit' | 'employee',
    elementId: string
  ): Promise<void> {
    const requestId = ++this.effectiveConfigRequestId;
    this.effectiveConfig = null;
    this.effectiveConfigError = '';
    this.isLoadingEffectiveConfig = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<EffectiveOperationalConfigResponse>(
          `operational-config/effective/${elementType}/${encodeURIComponent(elementId)}`
        )
      );
      if (requestId === this.effectiveConfigRequestId) {
        this.effectiveConfig = response;
        this.applyEffectiveConfigToInheritedForms();
      }
    } catch (error) {
      console.error('Error loading effective operational config for employee', error);
      if (requestId === this.effectiveConfigRequestId) {
        this.effectiveConfigError = this.t('employeesCatalog.provisioning.effectiveConfigError');
      }
    } finally {
      if (requestId === this.effectiveConfigRequestId) {
        this.isLoadingEffectiveConfig = false;
      }
    }
  }

  private async restoreInheritedEffectiveConfig(): Promise<void> {
    const elementType = this.entityForm.orgUnitId ? 'orgUnit' : 'companiesRecord';
    const elementId = this.entityForm.orgUnitId || this.entityForm.companiesRecordId;
    if (elementId) {
      await this.loadEffectiveConfig(elementType, elementId);
      return;
    }
    this.applyEffectiveConfigToInheritedForms();
  }

  private applyEffectiveConfigToInheritedForms(): void {
    if (!this.workAssignmentForm.enabled) {
      const effectiveWorkAssignment = this.effectiveConfig?.workAssignment.value || null;
      this.workAssignmentForm = this.createWorkAssignmentForm(effectiveWorkAssignment);
      this.workAssignmentForm.enabled = false;
      this.syncWorkAssignmentDatePickerValues();
      void this.loadSelectedWorkCycleDetail(this.workAssignmentForm.workCycleId);
      this.assignmentScheduleMonthIndex = 0;
    }

    if (!this.attendanceConfigForm.enabled) {
      const effectiveAttendanceConfig = this.displayedAttendanceConfig;
      this.attendanceConfigForm = mapAttendanceConfigToForm(effectiveAttendanceConfig);
      this.attendanceConfigForm.enabled = false;
    }
  }

  private buildEffectiveOriginLabel(
    block?: { configFrom: string; configFromId?: string | null } | null
  ): string {
    if (!block) {
      return this.t('operationalConfigInheritance.unavailable');
    }

    const sourceId = block.configFromId || '';
    if (block.configFrom === 'employee') {
      return this.t('operationalConfigInheritance.own.employee');
    }
    if (block.configFrom === 'orgUnit') {
      const name = this.orgUnits.find((unit) => unit.orgUnitId === sourceId)?.name || sourceId;
      return this.t('operationalConfigInheritance.inherited.orgUnit', { name });
    }
    if (block.configFrom === 'companiesRecord') {
      const name = this.companies.find((company) => company.companiesRecordId === sourceId)?.name || sourceId;
      return this.t('operationalConfigInheritance.inherited.company', { name });
    }
    if (block.configFrom === 'center') {
      return this.t('operationalConfigInheritance.inherited.center');
    }
    return this.t('operationalConfigInheritance.inherited.defaults');
  }

  private async loadBiometricTemplates(employeeId?: string | null): Promise<void> {
    if (!employeeId) {
      this.biometricTemplates = [];
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<EmployeeBiometricTemplatesResponse>(`employees/${employeeId}/biometric-templates`)
      );
      this.biometricTemplates = (response.data || []).filter((template) => template.isActive !== false);
    } catch (error) {
      console.error('Error loading employee biometric templates', error);
      this.biometricTemplates = [];
    }
  }

  private async loadMobileFaceProfile(employeeId?: string | null): Promise<void> {
    if (!employeeId) {
      this.mobileFaceProfile = null;
      this.isMobileFaceFormVisible = false;
      return;
    }

    this.isLoadingMobileFaceProfile = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<EmployeeFaceProfileRecord>(`employees/${employeeId}/face-profile`)
      );
      this.mobileFaceProfile = response || null;
    } catch (error) {
      console.error('Error loading employee mobile face profile', error);
      this.mobileFaceProfile = null;
    } finally {
      this.isLoadingMobileFaceProfile = false;
    }
  }

  toggleMobileFaceProfileForm(): void {
    this.isMobileFaceFormVisible = !this.isMobileFaceFormVisible;
    if (this.isMobileFaceFormVisible) {
      this.mobileFaceProfileForm = {
        embeddingText: '',
        embeddingSize: this.mobileFaceProfile?.embeddingSize ?? 128,
        sampleCount: this.mobileFaceProfile?.sampleCount ?? 1,
        threshold: this.mobileFaceProfile?.threshold ?? 0.8,
      };
    }
  }

  cancelMobileFaceProfileForm(): void {
    this.isMobileFaceFormVisible = false;
    this.mobileFaceProfileForm = {
      embeddingText: '',
      embeddingSize: this.mobileFaceProfile?.embeddingSize ?? 128,
      sampleCount: this.mobileFaceProfile?.sampleCount ?? 1,
      threshold: this.mobileFaceProfile?.threshold ?? 0.8,
    };
  }

  async saveMobileFaceProfile(): Promise<void> {
    if (!this.employeeId || this.isSavingMobileFaceProfile) {
      return;
    }

    const embedding = this.parseEmbeddingInput(this.mobileFaceProfileForm.embeddingText);
    if (!embedding.length) {
      this.showNotification(
        'snackbar-warning',
        this.t('employeesCatalog.notifications.mobileFaceInvalidEmbedding'),
        'bottom',
        'center'
      );
      return;
    }

    this.isSavingMobileFaceProfile = true;
    try {
      await firstValueFrom(
        this.apiService.put(`employees/${this.employeeId}/face-profile`, {
          embedding,
          embeddingSize: this.mobileFaceProfileForm.embeddingSize || 128,
          sampleCount: this.mobileFaceProfileForm.sampleCount || 1,
          threshold: this.mobileFaceProfileForm.threshold || 0.8,
        })
      );
      await this.loadMobileFaceProfile(this.employeeId);
      this.cancelMobileFaceProfileForm();
      this.showNotification(
        'snackbar-success',
        this.t('employeesCatalog.notifications.mobileFaceSaved'),
        'bottom',
        'center'
      );
    } catch (error) {
      console.error('Error saving employee mobile face profile', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('employeesCatalog.notifications.mobileFaceSaveError')),
        'bottom',
        'center'
      );
    } finally {
      this.isSavingMobileFaceProfile = false;
    }
  }

  async deleteMobileFaceProfile(): Promise<void> {
    if (!this.employeeId || !this.mobileFaceProfile || this.isDeletingMobileFaceProfile) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('employeesCatalog.biometricData.mobileFaceDeleteTitle'),
        message: this.t('employeesCatalog.biometricData.mobileFaceDeleteMessage'),
        confirmText: this.t('employeesCatalog.biometricData.mobileFaceDelete'),
        cancelText: this.t('common.cancel'),
      },
    });

    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }

    this.isDeletingMobileFaceProfile = true;
    try {
      await firstValueFrom(
        this.apiService.delete<EmployeeFaceProfileDeleteResponse>(`employees/${this.employeeId}/face-profile`)
      );
      this.mobileFaceProfile = null;
      this.cancelMobileFaceProfileForm();
      this.showNotification(
        'snackbar-success',
        this.t('employeesCatalog.notifications.mobileFaceDeleted'),
        'bottom',
        'center'
      );
    } catch (error) {
      console.error('Error deleting employee mobile face profile', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('employeesCatalog.notifications.mobileFaceDeleteError')),
        'bottom',
        'center'
      );
    } finally {
      this.isDeletingMobileFaceProfile = false;
    }
  }

  private parseEmbeddingInput(value: string): number[] {
    const raw = (value || '').trim();
    if (!raw) {
      return [];
    }

    try {
      const normalized = raw.startsWith('[') ? raw : `[${raw}]`;
      const parsed = JSON.parse(normalized);
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed.map((item) => Number(item)).filter((item) => Number.isFinite(item));
    } catch {
      return raw
        .split(/[\s,]+/)
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item));
    }
  }

  private async queueFingerprintEnrollment(result: FingerprintEnrollmentDialogResult): Promise<void> {
    this.isLoading = true;
    try {
      await firstValueFrom(
        this.apiService.post(`devices/${result.deviceId}/fingerprint-enrollments`, {
          employeeNumber: result.employeeNumber,
          fingerId: result.fingerId,
          retry: result.retry,
          overwrite: result.overwrite,
        })
      );
      this.showNotification(
        'snackbar-success',
        this.t('employeesCatalog.notifications.fingerprintQueued', { fingerId: result.fingerId }),
        'bottom',
        'center'
      );
    } catch (error) {
      console.error('Error queueing fingerprint enrollment', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('employeesCatalog.notifications.fingerprintQueueError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  private createEmptyEntityForm(): EmployeeFormValue {
    return {
      employeeNumber: '',
      firstName: '',
      lastName: '',
      secondLastName: '',
      email: '',
      hireDate: '',
      terminationDate: '',
      companiesRecordId: '',
      orgUnitId: '',
      status: 'active',
    };
  }

  private createEmptyWorkAssignmentForm(): WorkAssignmentFormValue {
    return {
      enabled: false,
      workCycleId: '',
      restConfigurationId: '',
      cycleStartDate: '',
      effectiveFrom: '',
      effectiveTo: '',
      toleranceRules: {
        entryToleranceMinutes: '',
        earlyExitToleranceMinutes: '',
        roundingMarginMinutes: '',
      },
      overtimePolicy: {
        ignoreOvertime: false,
      },
      turnPolicy: {
        enableAutoClose: false,
        autoCloseGraceMinutes: '',
      },
    };
  }

  private createWorkAssignmentForm(workAssignment: EmployeeWorkAssignment | null): WorkAssignmentFormValue {
    if (!workAssignment) {
      return this.createEmptyWorkAssignmentForm();
    }

    return {
      enabled: true,
      workCycleId: workAssignment.workCycleId || '',
      restConfigurationId: workAssignment.restConfigurationId || '',
      cycleStartDate: workAssignment.cycleStartDate || '',
      effectiveFrom: workAssignment.effectiveFrom || workAssignment.cycleStartDate || '',
      effectiveTo: workAssignment.effectiveTo || '',
      toleranceRules: {
        entryToleranceMinutes: workAssignment.toleranceRules?.entryToleranceMinutes ?? '',
        earlyExitToleranceMinutes: workAssignment.toleranceRules?.earlyExitToleranceMinutes ?? '',
        roundingMarginMinutes: workAssignment.toleranceRules?.roundingMarginMinutes ?? '',
      },
      overtimePolicy: {
        ignoreOvertime: !!workAssignment.overtimePolicy?.ignoreOvertime,
      },
      turnPolicy: {
        enableAutoClose: !!workAssignment.turnPolicy?.enableAutoClose,
        autoCloseGraceMinutes: workAssignment.turnPolicy?.autoCloseGraceMinutes ?? '',
      },
    };
  }

  private buildOrgUnitTree(orgUnits: OrgUnitRecord[]): OrgUnitPickerNode[] {
    const nodeMap = new Map<string, OrgUnitPickerNode>();
    const roots: OrgUnitPickerNode[] = [];

    const sortedUnits = [...orgUnits].sort((left, right) => {
      const levelDiff = (left.level || 0) - (right.level || 0);
      return levelDiff !== 0 ? levelDiff : left.name.localeCompare(right.name);
    });

    for (const orgUnit of sortedUnits) {
      nodeMap.set(orgUnit.orgUnitId, {
        orgUnitId: orgUnit.orgUnitId,
        name: orgUnit.name,
        type: orgUnit.type || '',
        level: orgUnit.level || 1,
        children: [],
        isLeaf: true,
        pathLabels: [],
      });
    }

    for (const orgUnit of sortedUnits) {
      const node = nodeMap.get(orgUnit.orgUnitId);
      if (!node) {
        continue;
      }

      const parentId = orgUnit.parentId || '';
      const parentNode = parentId ? nodeMap.get(parentId) : undefined;

      if (parentNode) {
        parentNode.children.push(node);
        parentNode.isLeaf = false;
      } else {
        roots.push(node);
      }
    }

    const assignPaths = (nodes: OrgUnitPickerNode[], parentPath: string[] = []): void => {
      nodes.sort((left, right) => left.name.localeCompare(right.name));
      nodes.forEach((node) => {
        node.pathLabels = [...parentPath, node.name];
        assignPaths(node.children, node.pathLabels);
      });
    };

    assignPaths(roots);
    return roots;
  }

  private findOrgUnitNodeById(nodes: OrgUnitPickerNode[], orgUnitId: string): OrgUnitPickerNode | null {
    for (const node of nodes) {
      if (node.orgUnitId === orgUnitId) {
        return node;
      }

      const childMatch = this.findOrgUnitNodeById(node.children, orgUnitId);
      if (childMatch) {
        return childMatch;
      }
    }

    return null;
  }

  private validateForm(): string | null {
    if (
      !this.entityForm.firstName.trim() ||
      !this.entityForm.lastName.trim() ||
      !this.entityForm.companiesRecordId ||
      !this.entityForm.orgUnitId
    ) {
      return this.t('employeesCatalog.notifications.validationError');
    }

    if (this.isEmployeeNumberRequired && !this.entityForm.employeeNumber.trim()) {
      return this.t('employeesCatalog.notifications.validationError');
    }

    if (this.isCreateMode && !this.entityForm.hireDate) {
      return this.t('employeesCatalog.notifications.validationHireDateRequired');
    }

    if (this.hasInvalidEmployeeNumberLength) {
      return this.t('employeesCatalog.notifications.validationEmployeeNumberLength', {
        length: this.employeeNumberPadLength,
      });
    }

    if (this.hasInvalidEmail) {
      return this.t('employeesCatalog.notifications.validationEmail');
    }

    if (this.isEmailRequired && !this.entityForm.email.trim()) {
      return this.t('employeesCatalog.notifications.validationEmailRequiredForMobile');
    }

    if (this.workAssignmentForm.enabled) {
      if (!this.workAssignmentForm.workCycleId) {
        return this.t('employeesCatalog.notifications.validationWorkCycle');
      }
      if (!this.workAssignmentForm.cycleStartDate) {
        return this.t('workAssignmentRules.validationCycleStartDate');
      }
      if (!this.hasValidWorkAssignmentDateRange()) {
        return this.t('workAssignmentRules.validationDateRange');
      }
    }

    if (this.canManageAttendanceConfig && this.attendanceConfigForm.enabled) {
      const attendanceConfigValidationKey = validateAttendanceConfigForm(this.attendanceConfigForm);
      if (attendanceConfigValidationKey) {
        return this.t(attendanceConfigValidationKey);
      }
    }

    return null;
  }

  private async persistAttendanceConfig(): Promise<void> {
    if (!this.employeeId || !this.canManageAttendanceConfig) {
      return;
    }

    if (!this.attendanceConfigForm.enabled) {
      if (this.attendanceConfig?.attendanceConfigId) {
        await firstValueFrom(this.apiService.delete(`attendance/configs/${this.attendanceConfig.attendanceConfigId}`));
      }
      this.attendanceConfig = null;
      this.attendanceConfigForm = createEmptyAttendanceConfigForm();
      return;
    }

    const payload = buildAttendanceConfigPayload(this.attendanceConfigForm, {
      employeeId: this.employeeId,
    });

    if (!payload) {
      return;
    }

    if (this.attendanceConfig?.attendanceConfigId) {
      this.attendanceConfig = await firstValueFrom(
        this.apiService.patch<AttendanceConfigRecord>(`attendance/configs/${this.attendanceConfig.attendanceConfigId}`, payload)
      );
      return;
    }

    this.attendanceConfig = await firstValueFrom(
      this.apiService.post<AttendanceConfigRecord>('attendance/configs', payload)
    );
  }

  private buildPayload(): EmployeeWithLaborConfigPayload {
    const employeeNumber = this.entityForm.employeeNumber.trim();
    const email = this.entityForm.email.trim();
    const shouldIncludeEmail = !this.isCreateMode || this.requiresMobileUser || !!this.effectiveConfigError;
    const hireDate = this.entityForm.hireDate || '';
    const terminationDate = this.entityForm.terminationDate || '';
    const payload: EmployeeWithLaborConfigPayload = {
      entity: {
        ...(employeeNumber ? { employeeNumber } : {}),
        firstName: this.entityForm.firstName.trim(),
        lastName: this.entityForm.lastName.trim(),
        secondLastName: this.entityForm.secondLastName.trim(),
        companiesRecordId: this.entityForm.companiesRecordId,
        orgUnitId: this.entityForm.orgUnitId,
        ...(shouldIncludeEmail && email ? { email } : {}),
        ...(hireDate ? { hireDate } : {}),
        ...(!this.isCreateMode && terminationDate ? { terminationDate } : {}),
      },
      deviceIds: [...this.selectedDeviceIds],
    };

    if (!this.isCreateMode) {
      payload.entity.status = this.entityForm.status;
    }

    if (this.workAssignmentForm.enabled) {
      payload.workAssignment = {
        workCycleId: this.workAssignmentForm.workCycleId || null,
        restConfigurationId: this.workAssignmentForm.restConfigurationId.trim() || null,
        cycleStartDate: this.workAssignmentForm.cycleStartDate,
        // cycleOffset is intentionally omitted until offset-based assignments are enabled again.
        ...(this.shouldSendEffectiveFrom() ? { effectiveFrom: this.workAssignmentForm.effectiveFrom } : {}),
        effectiveTo: this.workAssignmentForm.effectiveTo || null,
        toleranceRules: {
          entryToleranceMinutes: this.toNullableNumber(this.workAssignmentForm.toleranceRules.entryToleranceMinutes),
          earlyExitToleranceMinutes: this.toNullableNumber(this.workAssignmentForm.toleranceRules.earlyExitToleranceMinutes),
          roundingMarginMinutes: this.toNullableNumber(this.workAssignmentForm.toleranceRules.roundingMarginMinutes),
        },
        overtimePolicy: {
          ignoreOvertime: this.workAssignmentForm.overtimePolicy.ignoreOvertime,
        },
        turnPolicy: {
          enableAutoClose: this.workAssignmentForm.turnPolicy.enableAutoClose,
          ...(this.workAssignmentForm.turnPolicy.autoCloseGraceMinutes !== ''
            ? { autoCloseGraceMinutes: Number(this.workAssignmentForm.turnPolicy.autoCloseGraceMinutes) }
            : {}),
        },
      };
    } else if (!this.isCreateMode && this.workAssignment) {
      payload.workAssignment = null;
    }

    return payload;
  }

  private buildCreateAttendanceConfigPayload(): Record<string, unknown> | null {
    if (!this.canManageAttendanceConfig || !this.attendanceConfigForm.enabled) {
      return null;
    }

    const payload = buildAttendanceConfigPayload(this.attendanceConfigForm, {
      orgUnitId: this.entityForm.orgUnitId,
    });
    if (!payload) {
      return null;
    }

    const { scopeLevel: _scopeLevel, companiesRecordId: _companyId, orgUnitId: _orgUnitId, employeeId: _employeeId, ...config } = payload;
    return config;
  }

  private async showTemporaryCredentials(provisioning?: EmployeeUserProvisioning): Promise<void> {
    if (provisioning?.created !== true || !provisioning.temporaryPassword) {
      return;
    }

    const dialogRef = this.dialog.open(EmployeeTemporaryCredentialsDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      data: {
        account: provisioning.email || this.entityForm.email.trim(),
        accountLabel: this.t('employeesCatalog.fields.email'),
        password: provisioning.temporaryPassword,
        mustChangePassword: provisioning.mustChangePassword === true,
      },
    });
    await firstValueFrom(dialogRef.afterClosed());
  }

  private showNotification(colorName: string, text: string, placementFrom: MatSnackBarVerticalPosition, placementAlign: MatSnackBarHorizontalPosition): void {
    this.snackBar.open(text, '', { duration: 2200, verticalPosition: placementFrom, horizontalPosition: placementAlign, panelClass: colorName });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  private toNullableNumber(value: number | '' | null | undefined): number | null {
    return value === '' || value === null || value === undefined ? null : Number(value);
  }

  private isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  private buildAssignmentCalendarMonth(monthDate: Date, start: Date, end: Date): AssignmentCalendarMonth {
    const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const gridStart = this.startOfWeek(monthStart);
    const gridEnd = this.endOfWeek(monthEnd);
    const today = this.parseLocalDate(this.toIsoDateString(new Date()));
    const days: Array<AssignmentCalendarDay | null> = [];

    for (let cursor = new Date(gridStart); cursor <= gridEnd; cursor = this.addDays(cursor, 1)) {
      days.push({
        date: new Date(cursor),
        dayNumber: cursor.getDate(),
        isCurrentMonth: cursor.getMonth() === monthStart.getMonth(),
        isStart: this.isSameDate(cursor, start),
        isEnd: this.isSameDate(cursor, end),
        isInRange: cursor >= start && cursor <= end,
        isToday: !!today && this.isSameDate(cursor, today),
      });
    }

    return {
      label: new Intl.DateTimeFormat(this.getCalendarLocale(), {
        month: 'long',
        year: 'numeric',
      }).format(monthStart),
      days,
    };
  }

  private buildAssignmentScheduleCalendarMonth(
    monthDate: Date,
    start: Date,
    end: Date | null,
    cycleDays: WorkCyclePreviewDay[]
  ): AssignmentScheduleCalendarMonth {
    const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const gridStart = this.startOfWeek(monthStart);
    const gridEnd = this.endOfWeek(monthEnd);
    const today = this.parseLocalDate(this.toIsoDateString(new Date()));
    const weekdayFormatter = new Intl.DateTimeFormat(this.getCalendarLocale(), { weekday: 'long' });
    const days: AssignmentScheduleCalendarDay[] = [];

    for (let cursor = new Date(gridStart); cursor <= gridEnd; cursor = this.addDays(cursor, 1)) {
      const weekdayLabel = weekdayFormatter.format(cursor);
      days.push({
        date: new Date(cursor),
        dayNumber: cursor.getDate(),
        isCurrentMonth: cursor.getMonth() === monthStart.getMonth(),
        isStart: this.isSameDate(cursor, start),
        isEnd: !!end && this.isSameDate(cursor, end),
        isInRange: cursor >= start && (!end || cursor <= end),
        isToday: !!today && this.isSameDate(cursor, today),
        previewDay: cursor >= start && (!end || cursor <= end) ? this.getAppliedCycleDayForDate(cursor, cycleDays) : null,
        weekdayLabel: weekdayLabel.charAt(0).toUpperCase() + weekdayLabel.slice(1),
      });
    }

    return {
      label: new Intl.DateTimeFormat(this.getCalendarLocale(), {
        month: 'long',
        year: 'numeric',
      }).format(monthStart),
      days,
    };
  }

  private parseLocalDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }

    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private startOfWeek(value: Date): Date {
    const normalized = new Date(value.getFullYear(), value.getMonth(), value.getDate());
    const day = normalized.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    normalized.setDate(normalized.getDate() + mondayOffset);
    return normalized;
  }

  private endOfWeek(value: Date): Date {
    return this.addDays(this.startOfWeek(value), 6);
  }

  private addDays(value: Date, days: number): Date {
    const next = new Date(value.getFullYear(), value.getMonth(), value.getDate());
    next.setDate(next.getDate() + days);
    return next;
  }

  private isSameDate(left: Date, right: Date): boolean {
    return (
      left.getFullYear() === right.getFullYear() &&
      left.getMonth() === right.getMonth() &&
      left.getDate() === right.getDate()
    );
  }

  private getCalendarLocale(): string {
    return this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
  }

  private getDailyScheduleRangeLabel(schedule: DailyScheduleConfig | null): string {
    if (!schedule) {
      return this.t('employeesCatalog.workAssignment.cyclePreview.noHours');
    }

    if (schedule.mode === 'entry_window_duration') {
      if (schedule.entryWindowStartTime && schedule.entryWindowEndTime) {
        return `${schedule.entryWindowStartTime} - ${schedule.entryWindowEndTime}`;
      }
      return this.t('employeesCatalog.workAssignment.cyclePreview.flexibleWindow');
    }

    if (schedule.mode === 'split_shift') {
      if (schedule.blocks?.length) {
        return schedule.blocks.map((block) => `${block.startTime} - ${block.endTime}`).join(' · ');
      }
      return this.t('employeesCatalog.workAssignment.cyclePreview.splitShift');
    }

    if (schedule.startTime && schedule.endTime) {
      return `${schedule.startTime} - ${schedule.endTime}`;
    }

    if (schedule.startTime) {
      return schedule.startTime;
    }

    return this.t('employeesCatalog.workAssignment.cyclePreview.noHours');
  }

  private getDailyScheduleHoursLabel(schedule: DailyScheduleConfig | null): string {
    if (!schedule) {
      return this.t('employeesCatalog.workAssignment.cyclePreview.noHours');
    }

    const totalMinutes = this.getDailyScheduleIntervals(schedule).reduce((sum, interval) => sum + Math.max(0, interval.end - interval.start), 0);
    if (totalMinutes <= 0) {
      return this.t('employeesCatalog.workAssignment.cyclePreview.noHours');
    }

    const hours = totalMinutes / 60;
    return Number.isInteger(hours)
      ? this.t('employeesCatalog.workAssignment.cyclePreview.hoursWhole', { hours })
      : this.t('employeesCatalog.workAssignment.cyclePreview.hoursFraction', { hours: hours.toFixed(1) });
  }

  private getDailyScheduleIntervals(schedule: DailyScheduleConfig): Array<{ start: number; end: number }> {
    if (schedule.blocks?.length) {
      return schedule.blocks
        .map((block) => this.createMinuteInterval(block.startTime, block.endTime))
        .filter((interval): interval is { start: number; end: number } => !!interval);
    }

    if (schedule.startTime && schedule.endTime) {
      const interval = this.createMinuteInterval(schedule.startTime, schedule.endTime);
      return interval ? [interval] : [];
    }

    return [];
  }

  private createMinuteInterval(startTime: string | undefined, endTime: string | undefined): { start: number; end: number } | null {
    const start = this.timeToMinutes(startTime);
    const end = this.timeToMinutes(endTime);

    if (start === null || end === null) {
      return null;
    }

    return { start, end: end <= start ? end + 1440 : end };
  }

  private timeToMinutes(value: string | undefined): number | null {
    if (!value) {
      return null;
    }

    const [hours, minutes] = value.split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return null;
    }

    return hours * 60 + minutes;
  }

  private getAssignmentCyclePreviewReferenceDate(): Date | null {
    return (
      this.parseLocalDate(this.workAssignmentForm.effectiveFrom) ||
      this.parseLocalDate(this.workAssignmentForm.cycleStartDate) ||
      null
    );
  }

  private getAppliedCycleDayForDate(date: Date, cycleDays: WorkCyclePreviewDay[]): WorkCyclePreviewDay {
    const cycleLength = cycleDays.length;
    const cycleStartDate = this.parseLocalDate(this.workAssignmentForm.cycleStartDate) || this.getAssignmentCyclePreviewReferenceDate() || date;
    const daysDiff = Math.floor((date.getTime() - cycleStartDate.getTime()) / 86400000);
    const normalizedIndex = this.mod(daysDiff, cycleLength);
    const cycleDay = cycleDays[normalizedIndex] || cycleDays[0];

    return this.isRestConfiguredForDate(date, cycleDay)
      ? this.toConfiguredRestPreviewDay(cycleDay)
      : cycleDay;
  }

  private isRestConfiguredForDate(date: Date, cycleDay: WorkCyclePreviewDay): boolean {
    if (cycleDay.isRest) {
      return true;
    }

    const restConfiguration = this.getSelectedRestConfiguration();
    if (!restConfiguration || restConfiguration.isActive === false) {
      return false;
    }

    const mode = String(restConfiguration.mode || '').trim().toLowerCase();
    if (mode === 'fixed_weekday') {
      const weekday = date.getDay() === 0 ? 7 : date.getDay();
      return (restConfiguration.weekdays || []).map(Number).includes(weekday);
    }

    if (mode === 'cycle_reference') {
      if (
        restConfiguration.cycleReference &&
        restConfiguration.cycleReference !== this.workAssignmentForm.workCycleId
      ) {
        return false;
      }

      return (restConfiguration.restDays || []).map(Number).includes(cycleDay.dayOrder);
    }

    return false;
  }

  private getSelectedRestConfiguration(): RestConfigurationConfig | null {
    const restConfigurationId = this.workAssignmentForm.restConfigurationId;
    if (!restConfigurationId) {
      return null;
    }

    return this.restConfigurations.find(
      (item) => item.restConfigurationId === restConfigurationId
    ) || null;
  }

  private toConfiguredRestPreviewDay(cycleDay: WorkCyclePreviewDay): WorkCyclePreviewDay {
    return {
      ...cycleDay,
      dayType: 'rest',
      scheduleName: this.t('employeesCatalog.workAssignment.cyclePreview.rest'),
      scheduleRange: this.t('employeesCatalog.workAssignment.cyclePreview.noHours'),
      expectedHours: this.t('employeesCatalog.workAssignment.cyclePreview.noHours'),
      isRest: true,
    };
  }

  private updateWorkAssignmentDate(
    target: WorkAssignmentDateTarget,
    value: string
  ): void {
    const previousCycleStart = this.workAssignmentForm.cycleStartDate;
    this.workAssignmentForm[target] = value;
    if (
      target === 'cycleStartDate' &&
      (!this.workAssignmentForm.effectiveFrom || this.workAssignmentForm.effectiveFrom === previousCycleStart)
    ) {
      this.workAssignmentForm.effectiveFrom = value;
    }
    this.onWorkAssignmentDatesChange();
  }

  private parseCompactDateInput(
    event: Event
  ): { date: Date; displayValue: string; input: HTMLInputElement } | null {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) {
      return null;
    }

    const value = input.value.trim();
    if (!/^\d{8}$/.test(value)) {
      return null;
    }

    const day = Number(value.slice(0, 2));
    const month = Number(value.slice(2, 4));
    const year = Number(value.slice(4, 8));
    const date = new Date(year, month - 1, day);
    const isValid = date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day;

    if (!isValid) {
      return null;
    }

    return {
      date,
      displayValue: `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4, 8)}`,
      input,
    };
  }

  private syncDatePickerValues(): void {
    this.entityDatePickerValues = {
      hireDate: this.toPickerDate(this.entityForm.hireDate),
      terminationDate: this.toPickerDate(this.entityForm.terminationDate),
    };
    this.syncWorkAssignmentDatePickerValues();
  }

  private syncWorkAssignmentDatePickerValues(): void {
    this.workAssignmentDatePickerValues = {
      cycleStartDate: this.toPickerDate(this.workAssignmentForm.cycleStartDate),
      effectiveFrom: this.toPickerDate(this.workAssignmentForm.effectiveFrom),
      effectiveTo: this.toPickerDate(this.workAssignmentForm.effectiveTo),
    };
  }

  private hasValidWorkAssignmentDateRange(): boolean {
    const start = this.parseLocalDate(
      this.workAssignmentForm.effectiveFrom || this.workAssignmentForm.cycleStartDate
    );
    const end = this.parseLocalDate(this.workAssignmentForm.effectiveTo);
    return !start || !end || end.getTime() >= start.getTime();
  }

  private shouldSendEffectiveFrom(): boolean {
    return !!this.workAssignmentForm.effectiveFrom &&
      this.workAssignmentForm.effectiveFrom !== this.workAssignmentForm.cycleStartDate;
  }

  private captureEditableStateBaseline(): void {
    this.editableStateBaseline = this.serializeEditableState();
  }

  private serializeEditableState(): string {
    return JSON.stringify({
      entityForm: {
        ...this.entityForm,
        terminationDate: this.isCreateMode ? '' : this.entityForm.terminationDate,
      },
      workAssignmentForm: this.workAssignmentForm,
      attendanceConfigForm: this.attendanceConfigForm,
      selectedDeviceIds: [...this.selectedDeviceIds].sort(),
    });
  }

  private mod(value: number, base: number): number {
    if (!base) {
      return 0;
    }
    return ((value % base) + base) % base;
  }

  private toIsoDateString(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getBiometricTemplateLabel(template: EmployeeBiometricTemplateRecord): string {
    if (template.modality === 'fingerprint' && typeof template.templateSlot === 'number' && template.templateSlot >= 0 && template.templateSlot <= 9) {
      const hand = template.templateSlot <= 4 ? 'left' : 'right';
      const map: Record<number, string> = {
        0: 'little',
        1: 'ring',
        2: 'middle',
        3: 'index',
        4: 'thumb',
        5: 'thumb',
        6: 'index',
        7: 'middle',
        8: 'ring',
        9: 'little',
      };

      return this.t(`employeesCatalog.fingerprintEnrollment.fingers.${hand}.${map[template.templateSlot]}`);
    }

    if (template.modality === 'face') {
      return this.t('employeesCatalog.biometricData.faceLabel');
    }

    return template.modality || '-';
  }

  getBiometricTemplateTypeLabel(template: EmployeeBiometricTemplateRecord): string {
    if (template.modality === 'fingerprint') {
      return this.t('employeesCatalog.biometricData.fingerprint');
    }

    if (template.modality === 'face') {
      return this.t('employeesCatalog.biometricData.face');
    }

    return template.modality || '-';
  }

  getBiometricTemplateIcon(template: EmployeeBiometricTemplateRecord): string {
    if (template.modality === 'face') {
      return 'face';
    }

    return 'fingerprint';
  }

  formatDateTime(value?: string | null): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }
}
